import { buttonStyle } from 'components/buttons/buttonStyle';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import { CircleAlert, InfoOutlined, PlusIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import { SectionSaveRefused } from 'components/section-edit/SectionSaveRefused';
import PartyName from 'components/party/PartyName';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Button } from 'components/ui/button';
import { TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from 'components/ui/table';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { ReactNode, useCallback, useEffect, useReducer } from 'react';
import { Control, useFieldArray, useForm, UseFormReturn, UseFormSetValue, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useGetInvoiceTotalsQuery, useUpdateInvoicePayersMutation } from 'store/api/invoices';
import { ContactMinDto } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { InvoiceData, InvoicePayerDto } from 'types/invoices';
import { MinDto } from 'types/other';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { partyNameString } from 'utils/partyNameUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import {
    SHARE_TOTAL,
    formatShareRate,
    initialShareRateForNewPayer,
    isShareRateInRange,
    isShareRateOutOfRange,
    isShareTotalComplete,
    shareAmountFromRate,
    shareAmountsFromRates,
    shareRateFromAmount,
    shareValueToNumber,
    sumShareAmounts,
    sumShareRates,
} from './paymentShare';
import {
    ACTIONS_CELL_CLASS,
    AddPayerRow,
    FOOTER_CELL_CLASS,
    MoveToTopButton,
    NUMERIC_CELL_CLASS,
    NUMERIC_HEAD_CLASS,
    PAYER_CELL_CLASS,
    PAYER_HEAD_CLASS,
    PAYER_TABLE_CLASS,
    PAYER_TABLE_FRAME_CLASS,
    READ_ONLY_LINK_CELL_CLASS,
    ReadOnlyPayersNote,
    RemovePayerButton,
    ShareTotalIndicator,
    useRefusedDuplicatePick,
    ZERO_SHARE_ROW_CLASS,
} from './PayerTableParts';
import { ShareInput, ShareRateDisplay } from './ShareInput';

/** A row of the list in its order: the first row is the main payer, whose party is the invoice's payer. */
interface PayerRow {
    party: MinDto | null;
    shareRate: number | string;
    shareAmount: number | string;
}

export interface PayersForm {
    payers: PayerRow[];
}

function toPayerRows(payers: InvoicePayerDto[] | undefined): PayerRow[] {
    return (payers ?? []).map((payer) => ({
        party: payer.party,
        shareRate: payer.shareRate,
        shareAmount: payer.shareAmount,
    }));
}

function toPayersRequest(rows: PayerRow[]): { partyId: number; shareRate: number }[] {
    return rows.map((row) => ({
        partyId: row.party?.id ?? 0,
        shareRate: rows.length === 1 ? SHARE_TOTAL : shareValueToNumber(row.shareRate),
    }));
}

/** The list's problems the table reports so far: none of a row while it is still being filled in. */
interface ReportedPayerProblems {
    /** Once a share input has been left or a payer removed, not while the first share is typed. */
    isShareTotalReported: boolean;
    /** Once a share input or the row without a payer has been left, not as soon as the row is added. */
    isMissingPayerReported: boolean;
}

type PayersEditEvent = 'share-left' | 'empty-row-left' | 'payer-added' | 'payer-removed' | 'saved-list-loaded';

const NOTHING_REPORTED: ReportedPayerProblems = { isShareTotalReported: false, isMissingPayerReported: false };

function reportedAfter(reported: ReportedPayerProblems, event: PayersEditEvent): ReportedPayerProblems {
    switch (event) {
        case 'share-left':
            return { isShareTotalReported: true, isMissingPayerReported: true };
        case 'empty-row-left':
            return { ...reported, isMissingPayerReported: true };
        case 'payer-added':
            return { ...reported, isMissingPayerReported: false };
        case 'payer-removed':
            return { ...reported, isShareTotalReported: true };
        case 'saved-list-loaded':
            return NOTHING_REPORTED;
    }
}

export interface InvoicePayersFormState {
    methods: UseFormReturn<PayersForm>;
    rows: PayerRow[];
    savedPayersKey: string;
    reported: ReportedPayerProblems;
    report: (event: PayersEditEvent) => void;
}

/**
 * The invoice's payer list as an unsaved edit. The saved list is its source of truth: it comes back changed after
 * every save of the list, and only then, so an unfinished edit survives the other fields' auto-saves.
 */
export function useInvoicePayersForm(invoice: InvoiceData): InvoicePayersFormState {
    const savedPayersKey = JSON.stringify(toPayersRequest(toPayerRows(invoice.payers)));
    const methods = useForm<PayersForm>({ defaultValues: { payers: toPayerRows(invoice.payers) } });
    const { control, reset } = methods;
    const rows = useWatch({ control, name: 'payers' }) ?? [];
    const [reported, report] = useReducer(reportedAfter, NOTHING_REPORTED);

    useEffect(() => {
        reset({ payers: toPayerRows(invoice.payers) });
        report('saved-list-loaded');
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `invoice.payers` is read through its key
    }, [savedPayersKey, reset]);

    return { methods, rows, savedPayersKey, reported, report };
}

/**
 * A row's payer picker; a party pays only once, so its list leaves out the parties the other rows hold, and a pick
 * of one of them in its contact dialog is refused.
 */
function PayerPicker({
    control,
    index,
    setValue,
    excludedPartyIds,
    onDuplicatePick,
    inCard,
}: {
    control: Control<PayersForm>;
    index: number;
    setValue: UseFormSetValue<PayersForm>;
    excludedPartyIds: number[];
    onDuplicatePick: () => void;
    inCard: boolean;
}) {
    const isOffered = (option: { id: number }) => !excludedPartyIds.includes(option.id);
    const pickPayer = (name: `payers.${number}.party`, party: MinDto) => {
        if (isOffered(party)) {
            setValue(name, party);
        } else {
            onDuplicatePick();
        }
    };
    return (
        <ContactDynamicSelectPicker
            control={control}
            name={`payers.${index}.party`}
            setValue={pickPayer}
            placeholder="invoices.payers.select-payer"
            paginated={{
                fetch: useLazyGetMinContactsListWithFiltersQuery,
                searchField: ['name', 'partyNumber'],
                transformOptions: (options: ContactMinDto[]) => options.filter(isOffered),
            }}
            PickerModalComponent={ContactPickerModal}
            // In the card a row's search is as quiet as its other row actions.
            pickerButtonClassName={inCard ? 'text-foreground-secondary' : undefined}
        />
    );
}

function AddMultiplePayersButton({ onClick }: { onClick: () => void }) {
    return (
        <Button
            type="button"
            {...buttonStyle({ variant: 'text', size: 'extraSmall' }, '-ml-2 gap-1 self-start')}
            onClick={onClick}
        >
            <PlusIcon className="size-4" />
            <FormattedMessage id="invoices.payers.add-multiple" />
        </Button>
    );
}

function PayersTotalRow({
    net,
    currency,
    totalRate,
    isTotalComplete,
    totalAmount,
    amountColSpan,
}: {
    net: number;
    currency: string;
    totalRate: number;
    isTotalComplete: boolean;
    totalAmount: number;
    amountColSpan: number;
}) {
    return (
        <TableFooter className="border-t-0 bg-transparent">
            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                <TableCell className={FOOTER_CELL_CLASS}>
                    <FormattedMessage
                        id="invoices.payers.total-of-net"
                        values={{ amount: <MoneyDisplay amount={net} currency={currency} /> }}
                    />
                </TableCell>
                <TableCell className={cn(FOOTER_CELL_CLASS, 'text-right')}>
                    <ShareTotalIndicator total={totalRate} isComplete={isTotalComplete} />
                </TableCell>
                <TableCell className={cn(FOOTER_CELL_CLASS, 'text-right')} colSpan={amountColSpan}>
                    <MoneyDisplay amount={totalAmount} />
                </TableCell>
            </TableRow>
        </TableFooter>
    );
}

interface ValidationError {
    id: string;
    values?: Record<string, string>;
}

const PAYER_REQUIRED_ERROR: ValidationError = { id: 'invoices.payers.validation.payer-required' };
const DUPLICATE_PAYER_ERROR: ValidationError = { id: 'invoices.payers.validation.duplicate-payer' };
const SHARES_TOTAL_ERROR_ID = 'invoices.payers.validation.shares-total';

/**
 * The shares' problem, as a message id, or none when they add up to 100 within range. A share out of range is wrong
 * however the others end up, so it counts at once; a total off 100 only once it is reported.
 */
function sharesError(rows: PayerRow[], isTotalReported: boolean): ValidationError | null {
    const rates = rows.map((row) => row.shareRate);
    if (rows.length <= 1) {
        return null;
    }
    if (rates.some(isShareRateOutOfRange)) {
        return { id: 'invoices.payers.validation.share-range' };
    }
    if (isTotalReported && (!rates.every(isShareRateInRange) || !isShareTotalComplete(rates))) {
        return { id: SHARES_TOTAL_ERROR_ID, values: { total: formatShareRate(sumShareRates(rates)) } };
    }
    return null;
}

/** The first problem with the rows, as a message id, or none when the list can be saved. */
function payersValidationError(rows: PayerRow[]): ValidationError | null {
    if (rows.some((row) => !row.party)) {
        return PAYER_REQUIRED_ERROR;
    }
    const partyIds = rows.map((row) => row.party?.id);
    if (new Set(partyIds).size !== partyIds.length) {
        return DUPLICATE_PAYER_ERROR;
    }
    return sharesError(rows, true);
}

/** The problem the table shows under it: the first one reported so far, a refused pick being the latest. */
function shownPayersError(
    rows: PayerRow[],
    reported: ReportedPayerProblems,
    isDuplicatePickRefused: boolean
): ValidationError | null {
    if (isDuplicatePickRefused) {
        return DUPLICATE_PAYER_ERROR;
    }
    if (reported.isMissingPayerReported && rows.some((row) => !row.party)) {
        return PAYER_REQUIRED_ERROR;
    }
    return sharesError(rows, reported.isShareTotalReported);
}

/**
 * Saves the edited list at once, for a card that saves it on its Save: a list that cannot be saved is refused with
 * its first problem, which the table then shows too, and an unchanged one is not sent.
 */
export function useSaveInvoicePayers(
    invoice: InvoiceData,
    { methods, savedPayersKey, report }: InvoicePayersFormState
) {
    const [updateInvoicePayers] = useUpdateInvoicePayersMutation();
    return useCallback(async () => {
        const rows = methods.getValues('payers');
        const error = payersValidationError(rows);
        if (error) {
            report('share-left');
            throw new SectionSaveRefused(t(error.id, error.values));
        }
        const payers = toPayersRequest(rows);
        if (rows.length > 0 && JSON.stringify(payers) !== savedPayersKey) {
            await updateInvoicePayers({ id: invoice.id, payers }).unwrap();
        }
    }, [invoice.id, methods, report, savedPayersKey, updateInvoicePayers]);
}

interface Props {
    invoice: InvoiceData;
    payersForm: InvoicePayersFormState;
    /** Why several payers are shown, not edited: managing a list of payers needs its own permission. */
    readOnlyNoteId: string | null;
    /** A single payer is changed with the ordinary right to edit the invoice's payer. */
    isSinglePayerReadOnly: boolean;
    /** Turning the single payer into a list needs the permission to manage payers. */
    canAddPayers: boolean;
    /** What sits right of the field's label, as on the single payer field. */
    labelAction?: ReactNode;
    /** The single payer field's own extras: it is disabled while a used prepayment holds the payer. */
    isSinglePayerDisabled?: boolean;
    singlePayerTooltip?: JSX.Element;
    /** In a card of its own, which titles the list and saves it on its Save: no label of its own, no save on change. */
    inCard?: boolean;
    /** The card's read view: the list as it is saved, its first row captioned as the main payer. */
    isReadView?: boolean;
}

/**
 * The invoice's payer: one payer is the plain payer field, several are a table of their shares in place of it, so
 * removing all but one returns to the field. The first row is the main payer, the invoice's own payer, which takes
 * the rounding remainder. Shares save when their input is left; a change of the payers themselves saves at once,
 * both only while the list is valid. In a card of its own (`inCard`) nothing saves until the card's Save, which runs
 * {@link useSaveInvoicePayers}. Each payer's VAT, and so its payable amount, is known once the invoice is
 * finalized and split, so the table shows the shares of the net only.
 */
export function InvoicePayersField({
    invoice,
    payersForm,
    readOnlyNoteId,
    isSinglePayerReadOnly,
    canAddPayers,
    labelAction,
    isSinglePayerDisabled,
    singlePayerTooltip,
    inCard = false,
    isReadView = false,
}: Props) {
    const { methods, rows, savedPayersKey, reported, report } = payersForm;
    const { control, setValue, getValues, watch } = methods;
    const { isDuplicatePickRefused, refuseDuplicatePick } = useRefusedDuplicatePick(watch);
    const isSinglePayer = rows.length <= 1;
    const isReadOnly = isReadView || (isSinglePayer ? isSinglePayerReadOnly : readOnlyNoteId !== null);
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);
    const net = invoiceTotals?.invoiceCurrency.invoice.totalWithoutVat ?? 0;
    const currency = invoice.invoiceCurrencyTypeCode;
    const [updateInvoicePayers] = useUpdateInvoicePayersMutation();
    const { fields, append, replace } = useFieldArray({ control, name: 'payers', keyName: 'formKey' });

    const persist = useCallback(() => {
        const currentRows = getValues('payers');
        if (currentRows.length === 0 || payersValidationError(currentRows)) {
            return;
        }
        const payers = toPayersRequest(currentRows);
        if (JSON.stringify(payers) === savedPayersKey) {
            return;
        }
        updateInvoicePayers({ id: invoice.id, payers }).unwrap().catch(console.error);
    }, [getValues, invoice.id, savedPayersKey, updateInvoicePayers]);

    // A change of the payers themselves (picked in the field or in its modal, removed, moved to the top) has no blur
    // and saves as soon as the list is valid.
    const partyIdsKey = rows.map((row) => row.party?.id ?? '').join(',');
    useEffect(() => {
        if (!isReadOnly && !inCard) {
            persist();
        }
    }, [inCard, isReadOnly, partyIdsKey, persist]);

    const handleShareBlur = useCallback(() => {
        report('share-left');
        if (!inCard) {
            persist();
        }
    }, [inCard, persist, report]);

    const handleRateChange = useCallback(
        (index: number, value: string) => {
            const nextRates = getValues('payers').map((row, rowIndex) => (rowIndex === index ? value : row.shareRate));
            shareAmountsFromRates(net, nextRates).forEach((amount, rowIndex) => {
                setValue(`payers.${rowIndex}.shareAmount`, amount);
            });
        },
        [getValues, net, setValue]
    );

    const handleAmountChange = useCallback(
        (index: number, value: string) => {
            setValue(`payers.${index}.shareRate`, shareRateFromAmount(net, value));
        },
        [net, setValue]
    );

    const handleAddPayer = useCallback(() => {
        const rate = initialShareRateForNewPayer(getValues('payers').map((row) => row.shareRate));
        append({ party: null, shareRate: rate, shareAmount: shareAmountFromRate(net, rate) });
        report('payer-added');
    }, [append, getValues, net, report]);

    // The amounts are dealt again, as the rounding remainder belongs to whichever row is first now.
    const replaceInOrder = useCallback(
        (reordered: PayerRow[]) => {
            const amounts = shareAmountsFromRates(
                net,
                reordered.map((row) => row.shareRate)
            );
            replace(reordered.map((row, index) => ({ ...row, shareAmount: amounts[index] })));
        },
        [net, replace]
    );

    const handleRemovePayer = useCallback(
        (index: number) => {
            replaceInOrder(getValues('payers').filter((_, rowIndex) => rowIndex !== index));
            report('payer-removed');
        },
        [getValues, replaceInOrder, report]
    );

    const handleMoveToTop = useCallback(
        (index: number) => {
            const current = getValues('payers');
            replaceInOrder([current[index], ...current.filter((_, rowIndex) => rowIndex !== index)]);
        },
        [getValues, replaceInOrder]
    );

    if (isSinglePayer) {
        return (
            <div className="flex flex-col gap-1">
                <ContactDynamicSelectPicker
                    control={control}
                    name="payers.0.party"
                    label="invoices.general-tab.details.payer"
                    setValue={setValue}
                    getOptionLabel={(option) =>
                        partyNameString(option, { showPartyNumber: true, isPartyNumberUsageEnabled })
                    }
                    paginated={{
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                    }}
                    PickerModalComponent={ContactPickerModal}
                    required
                    disabled={isSinglePayerDisabled}
                    rightLabelComponent={labelAction}
                    tooltip={singlePayerTooltip}
                    readonly={isReadOnly ? { linkTo: 'contact' } : undefined}
                />
                {canAddPayers ? <AddMultiplePayersButton onClick={handleAddPayer} /> : null}
            </div>
        );
    }

    const rates = rows.map((row) => row.shareRate);
    const amounts = rows.map((row) => shareValueToNumber(row.shareAmount));
    const columnCount = isReadOnly ? 3 : 4;
    const headCells = [
        { id: 'payer', className: PAYER_HEAD_CLASS },
        { id: 'share-rate', className: cn(NUMERIC_HEAD_CLASS, 'w-[120px]') },
        { id: 'share-of-net', className: cn(NUMERIC_HEAD_CLASS, 'w-[150px]'), values: { currency } },
        ...(isReadOnly ? [] : [{ id: 'actions', className: cn(PAYER_HEAD_CLASS, 'w-20') }]),
    ];
    const shownError = isReadOnly ? null : shownPayersError(rows, reported, isDuplicatePickRefused);
    const isShareTotalInvalid =
        !isReadOnly && sharesError(rows, reported.isShareTotalReported)?.id === SHARES_TOTAL_ERROR_ID;

    return (
        <InputWrapper
            name="payers"
            label={inCard ? undefined : 'invoices.payers.title'}
            tooltip={inCard ? undefined : <PayersInfoTooltip />}
            rightLabelComponent={labelAction}
            hasErrors={false}
        >
            <div className="flex flex-col gap-2">
                {/* The card draws the table open on its surface, a rule under the header; elsewhere it is boxed. */}
                <div className={cn(!inCard && PAYER_TABLE_FRAME_CLASS, 'overflow-x-auto')}>
                    <table className={PAYER_TABLE_CLASS}>
                        <TableHeader
                            className={cn(
                                '[&_tr]:border-b-0',
                                inCard &&
                                    '[&_th]:border-divider [&_th]:text-foreground-tertiary [&_th]:bg-transparent [&_th]:font-medium'
                            )}
                        >
                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                {headCells.map((cell) => (
                                    <TableHead key={cell.id} className={cell.className}>
                                        <span className={cn(inCard && cell.id === 'actions' && 'sr-only')}>
                                            <FormattedMessage
                                                id={`invoices.payers.columns.${cell.id}`}
                                                values={cell.values}
                                            />
                                        </span>
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {fields.map((field, index) => {
                                const row = rows[index] ?? field;
                                const payerName = row.party?.name ?? '';
                                const hasNoInvoice = shareValueToNumber(rates[index]) === 0;
                                return (
                                    <TableRow
                                        key={field.formKey}
                                        className={cn(SIMPLE_TABLE_ROW_CLASS, hasNoInvoice && ZERO_SHARE_ROW_CLASS)}
                                        onBlur={(event) => {
                                            if (!row.party && !event.currentTarget.contains(event.relatedTarget)) {
                                                report('empty-row-left');
                                            }
                                        }}
                                    >
                                        <TableCell
                                            className={cn(
                                                PAYER_CELL_CLASS,
                                                'min-w-[260px]',
                                                isReadOnly && READ_ONLY_LINK_CELL_CLASS
                                            )}
                                        >
                                            {isReadOnly ? (
                                                <>
                                                    {row.party ? (
                                                        <PartyName party={row.party} isLink useLinkWithOrigin />
                                                    ) : null}
                                                    {isReadView && index === 0 ? (
                                                        <span className="text-muted-foreground text-caption block">
                                                            <FormattedMessage id="invoices.payers.main-payer" />
                                                        </span>
                                                    ) : null}
                                                </>
                                            ) : (
                                                <PayerPicker
                                                    control={control}
                                                    index={index}
                                                    setValue={setValue}
                                                    excludedPartyIds={rows.flatMap((other, otherIndex) =>
                                                        otherIndex !== index && other.party ? [other.party.id] : []
                                                    )}
                                                    onDuplicatePick={refuseDuplicatePick}
                                                    inCard={inCard}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell className={cn(NUMERIC_CELL_CLASS, 'w-[120px]')}>
                                            {isReadOnly ? (
                                                <ShareRateDisplay value={shareValueToNumber(rates[index])} />
                                            ) : (
                                                <ShareInput
                                                    control={control}
                                                    name={`payers.${index}.shareRate`}
                                                    kind="rate"
                                                    className="w-[120px]"
                                                    ariaLabel={t('invoices.payers.columns.share-rate')}
                                                    isInvalid={
                                                        isShareTotalInvalid || isShareRateOutOfRange(rates[index])
                                                    }
                                                    onValueChange={(value) => handleRateChange(index, value)}
                                                    onBlur={handleShareBlur}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell className={cn(NUMERIC_CELL_CLASS, 'w-[150px]')}>
                                            {isReadOnly ? (
                                                <MoneyDisplay amount={amounts[index]} />
                                            ) : (
                                                <ShareInput
                                                    control={control}
                                                    name={`payers.${index}.shareAmount`}
                                                    kind="amount"
                                                    className="w-[140px]"
                                                    ariaLabel={t('invoices.payers.columns.share-of-net', { currency })}
                                                    onValueChange={(value) => handleAmountChange(index, value)}
                                                    onBlur={handleShareBlur}
                                                />
                                            )}
                                        </TableCell>
                                        {!isReadOnly ? (
                                            <TableCell className={ACTIONS_CELL_CLASS}>
                                                <span className="inline-flex items-center gap-1">
                                                    {index > 0 ? (
                                                        <MoveToTopButton
                                                            label={t('invoices.payers.move-payer-to-top', {
                                                                name: payerName,
                                                            })}
                                                            onClick={() => handleMoveToTop(index)}
                                                        />
                                                    ) : null}
                                                    <RemovePayerButton
                                                        label={t('invoices.payers.remove-payer', { name: payerName })}
                                                        onClick={() => handleRemovePayer(index)}
                                                    />
                                                </span>
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                );
                            })}
                            {!isReadOnly ? <AddPayerRow columnCount={columnCount} onClick={handleAddPayer} /> : null}
                        </TableBody>
                        <PayersTotalRow
                            net={net}
                            currency={currency}
                            totalRate={sumShareRates(rates)}
                            isTotalComplete={isShareTotalComplete(rates)}
                            totalAmount={sumShareAmounts(amounts)}
                            amountColSpan={columnCount - 2}
                        />
                    </table>
                </div>
                {shownError && inCard ? (
                    <p className="text-danger-ink m-0 flex items-start gap-1.5 text-sm" role="alert">
                        <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
                        <FormattedMessage id={shownError.id} values={shownError.values} />
                    </p>
                ) : shownError ? (
                    <p className="text-caption text-error" role="alert">
                        <FormattedMessage id={shownError.id} values={shownError.values} />
                    </p>
                ) : null}
                {readOnlyNoteId ? <ReadOnlyPayersNote messageId={readOnlyNoteId} /> : null}
                <p className="text-caption text-fg-secondary flex items-center gap-1.5">
                    <InfoOutlined className="size-4 shrink-0" />
                    <FormattedMessage id="invoices.payers.prepayments-note" />
                </p>
            </div>
        </InputWrapper>
    );
}

/** What the list of payers is and how its shares round, beside its title. */
export function PayersInfoTooltip() {
    return <InfoTooltip text={['invoices.payers.description', 'invoices.payers.rounding-footnote']} />;
}
